/**
 * P2A.5 / P2A.6 — Navigation mode context + entitlement filter.
 *
 * The shell sidebar can render its menu in four modes:
 *
 *   • `isa95`     — buckets modules into ISA-95 layers (default, legacy)
 *   • `module`    — flat list, one section per module
 *   • `persona`   — only modules in the active persona workspace's
 *                   primaryModules + secondaryModules; sub-grouped by ISA-95
 *   • `industry`  — only modules in the active industry bundle's
 *                   includedModules; sub-grouped by ISA-95
 *
 * This context owns the MODE only. Entitlement resolution lives in
 * `useShellModuleAccess`, which calls the self-scoped batch route and
 * feeds `entitlementGate.ts`; the gate intersects the entitled set with
 * the mode-resolved set and fails closed while the answer is missing.
 * Keeping the two apart is deliberate — a presentation filter and an
 * authorization filter must not be able to substitute for each other.
 *
 * The active mode + persona + industry are persisted to localStorage so
 * the user lands in their preferred view across sessions.
 */
import * as React from 'react';
import type { IndustryTag, PersonaTag } from '@tyndo/types';
import { INDUSTRY_BUNDLES, PERSONA_WORKSPACES } from '@tyndo/types';
import { useApiClient } from '@tyndo/hooks';
import { resolveModeVisibleCatalogueIds } from './navModeVisibility';

export type NavMode = 'isa95' | 'module' | 'persona' | 'industry';

const MODE_KEY = 'tyndo.nav.mode';
const PERSONA_KEY = 'tyndo.nav.persona';
const INDUSTRY_KEY = 'tyndo.nav.industry';

interface NavModeContextValue {
  mode: NavMode;
  setMode: (m: NavMode) => void;
  persona: PersonaTag | null;
  setPersona: (p: PersonaTag | null) => void;
  industry: IndustryTag | null;
  setIndustry: (i: IndustryTag | null) => void;
}

const NavModeContext = React.createContext<NavModeContextValue | null>(null);

/**
 * Server-authoritative GxP flag key. Written from the tenant compliance profile
 * (`GET /api/v1/compliance/profile`, sourced from the tenant's IsRegulated flag)
 * so modules gate GxP on a server-issued value rather than the user-selected
 * industry. Mirrors controls-ui `SERVER_GXP_STORAGE_KEY`.
 */
const SERVER_GXP_KEY = 'tyndo.compliance.gxp';

/**
 * Relative path of the compliance-posture read. The shared API client prepends
 * `/api/v1`, so the literal must NOT repeat it.
 *
 * `GET /api/v1/compliance/profile` is registered with
 * `.RequireAuthorization(PolicyNames.AuthenticatedUser)`
 * (src/Modules/Tyndo.Module.Compliance/Endpoints/ComplianceEndpoints.cs) — it is
 * readable by ANY authenticated operator, but it is NOT anonymous. This used to
 * be fetched with a bare `fetch` carrying `credentials: 'include'` and no bearer,
 * which the gateway answers 401 to on every boot: the authoritative GxP flag was
 * therefore never written, and every consumer silently fell back to the
 * client-side, user-selected industry heuristic for a REGULATORY posture. Going
 * through the shared client attaches the session bearer + tenant header.
 */
const COMPLIANCE_PROFILE_PATH = '/compliance/profile';

export function NavModeProvider({ children }: { children: React.ReactNode }) {
  const apiClient = useApiClient();

  // Seed the authoritative GxP flag from the server on boot (and overwrite any
  // stale/tampered client value). Re-runs if the session or tenant changes,
  // because the client's identity is derived from exactly those two — a tenant
  // switch must not leave the previous tenant's posture in place.
  //
  // Best-effort by design: a failure leaves the prior value (or the transitional
  // industry fallback) in place and is NOT escalated, because the binding
  // regulatory enforcement is server-side regardless. It must not be silent
  // about being unauthenticated, though — that is the bug this replaced.
  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await apiClient.get<{ gxp?: boolean } | undefined>(
          COMPLIANCE_PROFILE_PATH,
        );
        if (cancelled) return;
        window.localStorage.setItem(SERVER_GXP_KEY, data?.gxp ? '1' : '0');
      } catch {
        /* no-op — fallback heuristic / server-side enforcement stands */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const [mode, setModeState] = React.useState<NavMode>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(MODE_KEY) : null;
    if (stored === 'isa95' || stored === 'module' || stored === 'persona' || stored === 'industry') return stored;
    return 'isa95';
  });
  const [persona, setPersonaState] = React.useState<PersonaTag | null>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(PERSONA_KEY) : null;
    return (stored as PersonaTag | null) ?? null;
  });
  const [industry, setIndustryState] = React.useState<IndustryTag | null>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(INDUSTRY_KEY) : null;
    return (stored as IndustryTag | null) ?? null;
  });

  const setMode = React.useCallback((m: NavMode) => {
    setModeState(m);
    try { window.localStorage.setItem(MODE_KEY, m); } catch { /* no-op */ }
  }, []);
  const setPersona = React.useCallback((p: PersonaTag | null) => {
    setPersonaState(p);
    try {
      if (p) window.localStorage.setItem(PERSONA_KEY, p);
      else window.localStorage.removeItem(PERSONA_KEY);
    } catch { /* no-op */ }
  }, []);
  const setIndustry = React.useCallback((i: IndustryTag | null) => {
    setIndustryState(i);
    try {
      if (i) window.localStorage.setItem(INDUSTRY_KEY, i);
      else window.localStorage.removeItem(INDUSTRY_KEY);
    } catch { /* no-op */ }
  }, []);

  const value = React.useMemo<NavModeContextValue>(
    () => ({
      mode,
      setMode,
      persona,
      setPersona,
      industry,
      setIndustry,
    }),
    [mode, setMode, persona, setPersona, industry, setIndustry]
  );

  return <NavModeContext.Provider value={value}>{children}</NavModeContext.Provider>;
}

export function useNavMode(): NavModeContextValue {
  const ctx = React.useContext(NavModeContext);
  if (!ctx) throw new Error('useNavMode must be used within a NavModeProvider');
  return ctx;
}

/**
 * Resolve the coarse set of moduleIds the active NAV MODE selects — the
 * persona workspace or the industry bundle. `null` means "this mode selects
 * nothing in particular" (isa95 / module modes list everything the principal
 * is entitled to), and so does a persona / industry that is unset or unknown.
 *
 * The returned ids are drawn from `catalogueModuleIds`, so they are in the same
 * id space `resolveModuleAccess` narrows. The manifests themselves name BARE
 * modules (`quality`) while the catalogue is provider-namespaced
 * (`tyndo.quality`); the translation lives in
 * `resolveModeVisibleCatalogueIds`, over the one `bareModuleId` helper the
 * shell already uses for this mismatch. Returning the manifest's bare names
 * directly — as this used to — intersects with the namespaced catalogue to the
 * empty set, which is what made persona and industry mode narrow every shell
 * surface down to nothing.
 *
 * This is a PRESENTATION filter, not an authorization one, and it deliberately
 * takes no entitlement argument: composing the two is the job of
 * `resolveModuleAccess` in `entitlementGate.ts`, which fails closed while the
 * entitlement answer is missing. The previous combined helper returned the
 * mode set alone when entitlements had not resolved, which is what made the
 * shell default-allow on first paint.
 */
export function resolveModeVisibleModules(
  mode: NavMode,
  persona: PersonaTag | null,
  industry: IndustryTag | null,
  catalogueModuleIds: readonly string[],
): Set<string> | null {
  if (mode === 'persona' && persona) {
    const ws = PERSONA_WORKSPACES.find((p) => p.id === persona);
    if (ws) {
      return resolveModeVisibleCatalogueIds(
        [...ws.primaryModules, ...ws.secondaryModules],
        catalogueModuleIds,
      );
    }
  }
  if (mode === 'industry' && industry) {
    const b = INDUSTRY_BUNDLES.find((x) => x.id === industry);
    if (b) return resolveModeVisibleCatalogueIds(b.includedModules, catalogueModuleIds);
  }
  return null;
}
